// `ToolCallRow` (beekeeper-agent-chat, `client-beekeeper-chat` "Tool calls render as collapsed
// rows that expand to their evidence"; sketch `prototype/sketches/beekeeper-agent-chat/
// tool-call-rows.html`). Presentational, styled only from token CSS vars — the same convention
// every other `ui/components/*` port follows.
//
// Collapsed by default; a failed `Edit` and an erroring `Validate` auto-expand, because a player
// must see those without asking. That auto-expand rule is UI POLICY, deliberately kept out of
// `ui/chat/transcript-model.ts` (which states only what happened, never how to draw it) — it is
// recomputed here from `tool`/`status` on every render, so it stays correct across a running row
// transitioning to error, not just at first mount. A manual tap always overrides it afterwards.
//
// `summary`/`detail` are rendered VERBATIM from the tool's own result (the server sends them,
// never a model paraphrase — client-beekeeper-chat) — this component invents no copy for them.

import { useState } from 'react';
import type { CSSProperties } from 'react';

export type ToolCallStatus = 'running' | 'ok' | 'error' | 'skipped';

export interface ToolCallRowProps {
  callId: string;
  tool: string;
  status: ToolCallStatus;
  summary: string;
  detail: string;
}

const GLYPH: Record<ToolCallStatus, string> = { running: '◌', ok: '✓', error: '✕', skipped: '—' };

/** A short display label for the wire's snake_case tool names — the four the agent actually holds
 * (`waggle-beekeeper/src/agent/tools.rs`). */
const TOOL_LABEL: Record<string, string> = {
  read_scripts: 'Read scripts',
  diff_scripts: 'Diff',
  edit_scripts: 'Edit',
  validate_scripts: 'Validate',
};

/** Anything else is **humanized**, never printed raw: `write_script` reads as "Write script", not
 * as an identifier out of the server's source. Mirrors `TerminalRow`'s `humanizeReason` — a lookup
 * with a legible fallback, so a tool added server-side never leaks snake_case at the player. */
function toolLabel(tool: string): string {
  const known = TOOL_LABEL[tool];
  if (known !== undefined) {
    return known;
  }
  const spaced = tool
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  return spaced.length === 0 ? 'Tool' : spaced[0]!.toUpperCase() + spaced.slice(1);
}

/** The two kinds that must be visible without the player asking (client-beekeeper-chat). */
function autoExpands(tool: string, status: ToolCallStatus): boolean {
  return status === 'error' && (tool === 'edit_scripts' || tool === 'validate_scripts');
}

const TONE: Record<ToolCallStatus, CSSProperties> = {
  running: { borderColor: 'var(--honey-300)', background: 'var(--honey-100)' },
  ok: {},
  error: { borderColor: 'var(--danger)', background: 'var(--danger-soft)' },
  skipped: { opacity: 0.55 },
};

const GLYPH_TONE: Record<ToolCallStatus, string> = {
  running: 'var(--honey-600)',
  ok: 'var(--success)',
  error: 'var(--danger)',
  skipped: 'var(--ink-3)',
};

export function ToolCallRow({ tool, status, summary, detail }: ToolCallRowProps) {
  const [manualExpanded, setManualExpanded] = useState<boolean | null>(null);
  const expanded = manualExpanded ?? autoExpands(tool, status);

  function toggle(): void {
    setManualExpanded(!expanded);
  }

  return (
    <div>
      <div
        data-testid="tool-row"
        data-tool={tool}
        data-status={status}
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          minHeight: 'var(--hit-target)',
          boxSizing: 'border-box',
          background: 'var(--surface-sunken)',
          // Longhands, not the `border` shorthand: `TONE[status]` spreads a `borderColor` over
          // this, and React warns (correctly) when a shorthand and one of its longhands both
          // change across renders, because the resolution order between them is not guaranteed.
          // A running row re-rendering to `ok` hit exactly that.
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: 'var(--line)',
          borderRadius: 'var(--r-sm)',
          padding: '7px 10px',
          cursor: 'pointer',
          ...TONE[status],
        }}
      >
        <span aria-hidden="true" style={{ flex: 'none', width: 14, textAlign: 'center', fontSize: 11, color: GLYPH_TONE[status] }}>
          {GLYPH[status]}
        </span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 500, color: status === 'error' ? 'var(--danger)' : 'var(--ink-1)' }}>
          {toolLabel(tool)}
        </span>
        <span
          data-testid="tool-row-summary"
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: status === 'running' ? 'var(--honey-700)' : 'var(--ink-3)',
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {summary}
        </span>
        <span aria-hidden="true" style={{ flex: 'none', fontSize: 10, color: 'var(--ink-3)' }}>
          {expanded ? '▾' : '▸'}
        </span>
      </div>
      {expanded && (
        <pre
          data-testid="tool-detail"
          style={{
            margin: '5px 0 0',
            background: status === 'error' ? 'var(--danger-soft)' : 'var(--hud-bg-solid)',
            color: status === 'error' ? '#7E2F1D' : '#D9CBAA',
            border: status === 'error' ? '1px solid var(--danger)' : 'none',
            borderRadius: 'var(--r-sm)',
            padding: '9px 11px',
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            lineHeight: 1.6,
            overflowX: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {detail}
        </pre>
      )}
    </div>
  );
}
